import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ServerStatusBadge from 'components/ServerStatusBadge';
import DomainLine from 'components/ServersList/DomainLine';
import type { AssetDetails } from 'modules/queries/asset/useDetails';

export interface RowIdentityProps extends BoxProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

/** Status badge, name, primary address, and domain for a server list row. */
const RowIdentity = ({
	assetDetails,
	notification,
	sx,
	...rest
}: RowIdentityProps) => {
	const {
		custom_name: customName,
		domain,
		ip,
		isPrivateParent,
		powerStatus,
		status,
	} = assetDetails;
	const { last_alert: lastAlert, description } = notification || {};

	return (
		<Box
			sx={combineSx(
				{
					display: 'flex',
					gap: 200,
					alignItems: 'center',
					maxWidth: '320px',
					flex: '0 0 auto',
					minWidth: 0,
				},
				sx,
			)}
			{...rest}
		>
			<ServerStatusBadge
				status={status}
				powerStatus={powerStatus}
				notificationPrimary={description}
				notificationSecondary={lastAlert || ''}
				sx={{ position: 'absolute' }}
			/>

			<Stack sx={{ gap: 100, flex: '1 0 0', minWidth: 0, marginLeft: '40px' }}>
				<Box sx={{ display: 'flex', gap: 100, alignItems: 'center' }}>
					<TypographyOverflow variant="label-lg" sx={{ fontWeight: 500 }}>
						{customName || domain}
					</TypographyOverflow>

					{!isPrivateParent && (
						<Typography
							variant="label-md"
							sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
						>
							{ip}
						</Typography>
					)}
				</Box>

				<DomainLine domain={domain} />
			</Stack>
		</Box>
	);
};

export default RowIdentity;
